import React, { useEffect, useState } from 'react';
import { Users, Calendar, Activity, BedDouble, Building2, BarChart3, Settings, Bell } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import DashboardLayout from '../../components/common/DashboardLayout';
import { StatCard, Card, LoadingState, Badge } from '../../components/common/UI';
import { analyticsAPI } from '../../services/api';
import toast from 'react-hot-toast';

export const NAV_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: Activity },
  { path: '/admin/users', label: 'User Management', icon: Users },
  { path: '/admin/appointments', label: 'Appointments', icon: Calendar },
  { path: '/admin/beds', label: 'Bed Management', icon: BedDouble },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/centers', label: 'Centers', icon: Building2 },
];

const DOSHA_COLORS = { Vata: '#60a5fa', Pitta: '#f97316', Kapha: '#34d399', Tridosha: '#a78bfa' };
const STATUS_COLORS = { scheduled: '#60a5fa', confirmed: '#14b8a6', completed: '#22c55e', cancelled: '#ef4444', 'in-progress': '#f59e0b', 'no-show': '#94a3b8' };
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.get()
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="admin"><LoadingState message="Loading analytics..." /></DashboardLayout>;

  const stats = data?.stats || {};
  const charts = data?.charts || {};

  // Format weekly chart
  const weeklyData = DAYS.map((day, i) => ({
    day,
    appointments: charts.weeklyAppointments?.find(w => w._id === i + 1)?.count || 0
  }));

  // Format dosha pie data
  const doshaData = (charts.therapyDist || []).map(d => ({
    name: d._id,
    value: d.count,
    color: DOSHA_COLORS[d._id] || '#94a3b8'
  }));

  // Format status data
  const statusData = (charts.appointmentsByStatus || []).map(s => ({
    name: s._id,
    value: s.count,
    color: STATUS_COLORS[s._id] || '#94a3b8'
  }));

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="admin">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Platform overview & analytics</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Patients" value={stats.totalPatients || 0} subtitle="Active patients" icon={Users} color="emerald" trend={12} />
        <StatCard title="Total Doctors" value={stats.totalDoctors || 0} subtitle="Active doctors" icon={Activity} color="blue" />
        <StatCard title="Appointments" value={stats.monthAppointments || 0} subtitle="This month" icon={Calendar} color="violet" trend={8} />
        <StatCard title="Bed Occupancy" value={`${stats.occupancyRate || 0}%`} subtitle={`${stats.bedsAvailable || 0} available`} icon={BedDouble} color="amber" />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-4 text-white">
          <p className="text-emerald-100 text-sm">Completed This Month</p>
          <p className="text-3xl font-bold mt-1">{stats.completedThisMonth || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-4 text-white">
          <p className="text-blue-100 text-sm">Total Appointments</p>
          <p className="text-3xl font-bold mt-1">{stats.totalAppointments || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-4 text-white">
          <p className="text-violet-100 text-sm">Total Beds</p>
          <p className="text-3xl font-bold mt-1">{stats.bedsTotal || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-4 text-white">
          <p className="text-amber-100 text-sm">Occupied Beds</p>
          <p className="text-3xl font-bold mt-1">{(stats.bedsTotal || 0) - (stats.bedsAvailable || 0)}</p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Weekly appointments bar */}
        <Card title="Weekly Appointments" subtitle="Last 7 days activity">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={weeklyData} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
              <Bar dataKey="appointments" fill="url(#appointmentGrad)" radius={[6, 6, 0, 0]} />
              <defs>
                <linearGradient id="appointmentGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Dosha distribution pie */}
        <Card title="Patient Dosha Distribution" subtitle="Constitution breakdown">
          {doshaData.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="60%" height={220}>
                <PieChart>
                  <Pie data={doshaData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value">
                    {doshaData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2.5 flex-1">
                {doshaData.map(d => (
                  <div key={d.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                    <span className="text-sm text-gray-700 flex-1">{d.name}</span>
                    <span className="text-sm font-bold text-gray-800">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-gray-400 text-sm text-center py-12">No patient data yet</p>}
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Appointment status */}
        <Card title="Appointments by Status" subtitle="All-time distribution">
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={statusData} layout="vertical" barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-sm text-center py-12">No appointment data yet</p>}
        </Card>

        {/* Quick stats table */}
        <Card title="Quick Insights" subtitle="Key performance indicators">
          <div className="space-y-3">
            {[
              { label: 'Therapy Completion Rate', value: stats.totalAppointments ? `${Math.round((stats.completedThisMonth / stats.monthAppointments) * 100) || 0}%` : '—', color: 'emerald' },
              { label: 'Bed Occupancy Rate', value: `${stats.occupancyRate || 0}%`, color: stats.occupancyRate > 80 ? 'red' : 'emerald' },
              { label: 'Doctor-Patient Ratio', value: stats.totalDoctors ? `1:${Math.round(stats.totalPatients / stats.totalDoctors)}` : '—', color: 'blue' },
              { label: 'Monthly Sessions', value: stats.monthAppointments || 0, color: 'violet' },
              { label: 'Available Beds', value: stats.bedsAvailable || 0, color: 'teal' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm text-gray-600">{item.label}</span>
                <Badge color={item.color}>{item.value}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
